import Bluebird from 'bluebird';
import moment from 'moment';

import { tokenInfo } from '../lib/tokens';
import { convertTokenValue, notNull, removeNulls } from '../utils/core';
import { chainInfo } from './chains';
import { serverRequest } from './index';
import { getTokenMetadata } from './tokens';

const ercTypeLabel = ({ ercType = '20' } = {}) => ({
  721: 'NFT',
  // 1155: '1155',
}[ercType] || '');

const getTokenInfo = async (token) => {
  const { name, symbol, tokenUri, units } = token;
  const {
    label,
    img = 'ethereum.png',
  } = tokenInfo[`${symbol}`.toUpperCase()] || {};
  let metadata = { image: img, preview: img };
  if (tokenUri) {
    try {
      metadata = await getTokenMetadata(tokenUri);
    } catch (e) {
      console.warn(`Failed to retrieve metadata for token URI: ${tokenUri}`, e);
    }
  }
  return {
    ...token,
    ercTypeLabel: ercTypeLabel(token),
    img,
    symbol,
    label: label || name || symbol || 'Unknown',
    metadata,
    value: units,
  };
};

const mapTransferSummary = async ({
  origin,
  destination,
  originName = 'Unknown',
  destinationName = 'Unknown',
  eventCount,
  completed,
  started,
  updated,
  ercType,
  events = [],
  token = {},
  ...rest
}) => {
  const {
    [origin]: { img: originImg = 'ethereum.png' } = {},
    [destination]: { img: destImg = 'ethereum.png' } = {},
  } = chainInfo;
  return ({
    ...rest,
    // @TODO - need to check that the transfer wasn't a failure
    status: completed ? 'success' : 'pending',
    origin: { label: originName, img: originImg, name: origin },
    destination: { label: destinationName, img: destImg, name: destination },
    token: await getTokenInfo({ ...token, ercType }, events),
    started: moment(started),
    completed: completed ? moment(updated) : undefined,
    duration: completed ? moment(updated).diff(moment(started)) : undefined,
  });
};

const mapTransfer = async ({
  transferId,
  origin,
  destination,
  originName = 'Unknown',
  destinationName = 'Unknown',
  token = {},
  ercType,
  logs,
  chain,
  latestEvent,
  completed,
  events = [],
  started,
  updated,
  ...rest
}) => {
  const {
    [origin]: { img: originImg = 'ethereum.png' } = {},
    [destination]: { img: destImg = 'ethereum.png' } = {},
  } = chainInfo;
  const [{ pending } = {}] = events;
  // @TODO - figure out a better way to infer source and destination chain
  // const getChainSide = (chain) => origin && origin === chain ? 'source' : 'destination';
  const getChainSide = (chain) => ({
    [origin]: 'source',
    [destination]: 'destination',
  }[chain]);
  logs = logs.filter(({ eventName }) => eventName).map(({
    chain,
    args,
    // args: { value } = {},
    token: { value } = {},
    token = {},
    decimals = 18,
    eventName,
    ...log
  }) => ({
    ...log,
    // Infer whether type 'source' or 'destination' for each log
    chainSide: getChainSide(chain),
    chain,
    args,
    token,
    eventName,
    // For transfers, extract and convert value
    ...(eventName === 'Transfer' && { value: value ? convertTokenValue(value, decimals) : '1' }),
  }));
  return {
    ...rest,
    id: transferId,
    chain,
    ercType,
    token: await getTokenInfo({ ...token, ercType }, events),
    origin: { label: originName, img: originImg, name: origin },
    destination: { label: destinationName, img: destImg, name: destination },
    logs,
    latestEvent,
    events,
    started: moment(started),
    updated: moment(updated),
    completed: completed ? moment(updated) : undefined,
    pending,
    duration: completed ? moment(updated).diff(moment(started)) : undefined,
  };
};

const dateRangeParameters = ({ startDate, endDate } = {}) => removeNulls({
  startDate: notNull(startDate) ? moment(startDate).startOf('day').format() : undefined,
  endDate: notNull(endDate) ? moment(endDate).endOf('day').format() : undefined,
});

export const fetchTransferSummaries = async (evt = {}) => {
  const { address = '', active, limit = 10, offset = 0, startDate, endDate } = evt;
  const transferSummaries = await serverRequest(`accounts/${address.trim()}/transfers`, {
    params: { active, limit, offset, ...dateRangeParameters({ startDate, endDate }) },
  });
  return Bluebird.map(transferSummaries, mapTransferSummary);
};

export const countTransferSummaries = async ({ address = '', startDate, endDate }) => {
  const response = await serverRequest(`accounts/${address.trim()}/transfers/count`, {
    params: dateRangeParameters({ startDate, endDate }),
  });
  const { count = 0 } = response || {};
  return count;
};

export const fetchTransfer = async ({ id }, map = false) => {
  const transfer = await serverRequest(`transfers/${id}`);
  return map ? mapTransfer(transfer) : transfer;
};

export const fetchActiveTransfers = async ({ address }) => {
  const transferSummaries = await fetchTransferSummaries({ address, active: true });
  // Filter the transfers that are incomplete
  // @TODO - will have to potentially filter out failed (and not retried) transfers
  // @TODO - parameterize this at API level
  const activeTransferSummaries = transferSummaries
    .filter(({ completed }) => !completed);
  const activeTransfers = await Bluebird.map(
    activeTransferSummaries,
    // destination get's mapped as an object in summary - causing havoc if missing
    async ({ transferId, destination, ...transferSummary }) => ({
      transferId,
      ...transferSummary,
      ...(await fetchTransfer({ id: transferId })),
    }),
    { concurrency: 3 },
  );
  return Bluebird.map(activeTransfers, mapTransfer);
};
